/**
 * CR-7264: supplier-group totals above the member sites on Supplier Performance.
 *
 * Filtering to a group already narrows the table to that group's sites. What it
 * could not do is answer "how is the GROUP performing" — the sites were listed
 * separately with no total, so the buyer had to add it up by eye.
 *
 * The total is shown **only while the Supplier Group filter is set** (see
 * `PerformanceTable`). Unfiltered, this page is a ranking of suppliers against
 * each other, and banding a total in above every group would interrupt that
 * ranking with figures nobody asked for.
 *
 * ## Why the totals are recomputed rather than averaged
 *
 * Counts add up. Ratios do not. PPM and OTIF% are ratios, and averaging a
 * group's member percentages weights a tiny site the same as a huge one:
 *
 *   POETON POLSKA    219,112 received     966 rejected   =  4,409 PPM
 *   POETON CARDIFF     4,086 received     170 rejected   = 41,605 PPM
 *
 *   average of the two PPMs .................... 23,007 PPM
 *   recomputed from the summed totals ...........  5,090 PPM
 *
 * The average overstates the group's defect rate by 4.5x, purely because
 * Cardiff is 54x smaller and gets equal billing. So every ratio here is
 * recomputed from the summed numerator and denominator, and no member
 * percentage is ever averaged.
 *
 * A metric whose denominator is zero is null, not zero — "no lines to score"
 * and "scored zero" are different answers and must not look alike.
 */
import { SupplierProfile } from '../../lib/api';

/** A group total plus the member rows it was computed from. */
export interface SupplierGroupAggregate {
  groupId: number;
  groupName: string;
  members: SupplierProfile[];
  /** Summed counts. */
  qtyReceived: number;
  rejectQty: number;
  ncrCount: number;
  returnCount: number;
  poLineCount: number;
  otifCount: number;
  arrearsCount: number;
  grnCount: number;
  ontimeCount: number;
  lateCount: number;
  earlyCount: number;
  /** Summed money in the display currency (Customer review 11 Sep, msg 10). */
  totalValue: number;
  totalCostOfQuality: number;
  /** Ratios, recomputed from the sums above. Null when the denominator is 0. */
  totalPpm: number | null;
  customerLiabilityPpm: number | null;
  supplierLiabilityPpm: number | null;
  otifPercentage: number | null;
  onTimePercentage: number | null;
}

/** Sum a numeric field across rows, treating absent as zero. */
function sum(rows: SupplierProfile[], pick: (r: SupplierProfile) => number | undefined): number {
  return rows.reduce((acc, r) => acc + (pick(r) ?? 0), 0);
}

/** A money field (decimal string or number from the API) as a number. */
function amount(value: string | number | null | undefined): number {
  const n = typeof value === 'number' ? value : parseFloat(value ?? '0');
  return Number.isFinite(n) ? n : 0;
}

/**
 * A ratio recomputed from totals.
 *
 * @param numerator - summed numerator
 * @param denominator - summed denominator
 * @param scale - 1e6 for PPM, 100 for a percentage
 * @returns the ratio, or null when there is nothing to divide by
 */
function ratio(numerator: number, denominator: number, scale: number): number | null {
  if (!denominator) return null;
  return (numerator / denominator) * scale;
}

/**
 * Roll a set of supplier rows up into one total per supplier group.
 *
 * Rows with no group are not invented into one — a supplier that belongs to no
 * group has no group total to show, and is left to render on its own.
 *
 * @param suppliers - the rows currently in the table
 * @returns one aggregate per group, ordered by group name
 */
export function aggregateBySupplierGroup(
  suppliers: SupplierProfile[],
): SupplierGroupAggregate[] {
  const byGroup = new Map<number, SupplierProfile[]>();
  for (const s of suppliers) {
    const id = s.supplier_group_id;
    if (id === null || id === undefined) continue;
    const bucket = byGroup.get(id);
    if (bucket) bucket.push(s);
    else byGroup.set(id, [s]);
  }

  const out: SupplierGroupAggregate[] = [];
  byGroup.forEach((members, groupId) => {
    const qtyReceived = sum(members, (r) => r.qty_received);
    const rejectQty = sum(members, (r) => r.reject_qty);
    const poLineCount = sum(members, (r) => r.po_line_count);
    const otifCount = sum(members, (r) => r.otif_count);
    const grnCount = sum(members, (r) => r.grn_count);
    const ontimeCount = sum(members, (r) => r.ontime_count);

    // Liability PPMs share the received quantity as their denominator, so they
    // are reconstructed from each member's own rejected-quantity share rather
    // than from the member percentages.
    const custRejected = members.reduce(
      (acc, r) => acc + ((r.customer_liability_ppm ?? 0) / 1_000_000) * (r.qty_received ?? 0), 0);
    const supRejected = members.reduce(
      (acc, r) => acc + ((r.supplier_liability_ppm ?? 0) / 1_000_000) * (r.qty_received ?? 0), 0);

    out.push({
      groupId,
      groupName: members[0]?.supplier_group_name ?? 'Group',
      members,
      qtyReceived,
      rejectQty,
      ncrCount: sum(members, (r) => r.ncr_count),
      returnCount: sum(members, (r) => r.return_count),
      poLineCount,
      otifCount,
      arrearsCount: sum(members, (r) => r.arrears_count),
      grnCount,
      ontimeCount,
      lateCount: sum(members, (r) => r.late_count),
      earlyCount: sum(members, (r) => r.early_count),
      totalValue: sum(members, (r) => amount(r.total_value)),
      totalCostOfQuality: sum(members, (r) => amount(r.total_cost_of_quality)),
      totalPpm: ratio(rejectQty, qtyReceived, 1_000_000),
      customerLiabilityPpm: ratio(custRejected, qtyReceived, 1_000_000),
      supplierLiabilityPpm: ratio(supRejected, qtyReceived, 1_000_000),
      otifPercentage: ratio(otifCount, poLineCount, 100),
      onTimePercentage: ratio(ontimeCount, grnCount, 100),
    });
  });

  return out.sort((a, b) => a.groupName.localeCompare(b.groupName));
}


/** One entry in the rendered table: a group total, or one supplier row. */
export type PerformanceRow =
  | { kind: 'group'; group: SupplierGroupAggregate }
  | { kind: 'supplier'; supplier: SupplierProfile };

/**
 * Interleave group totals with their member rows, in render order.
 *
 * Only called once the table is filtered to a group, so in practice this
 * returns one total followed by that group's sites. It stays general because
 * the filter narrows by group id and says nothing about how many groups the
 * loaded page happens to contain.
 *
 * Members are kept beneath their own group total and in their original order;
 * suppliers with no group follow, untouched.
 *
 * Customer review 11 Sep (item 4): a group of ONE gets its total too. The
 * group-of-one exception made the reviewer's test group — AMA, holding a supplier
 * alone — look as if the filter did nothing, where the old portal (her m04b)
 * puts the group row above its member whatever the count. The user asked for
 * the group; the page answers with the group.
 *
 * @param suppliers - the rows currently in the table
 * @returns group totals interleaved with the rows they summarise
 */
export function withGroupTotals(suppliers: SupplierProfile[]): PerformanceRow[] {
  const groups = aggregateBySupplierGroup(suppliers);
  const claimed = new Set<SupplierProfile>();
  const out: PerformanceRow[] = [];

  for (const group of groups) {
    out.push({ kind: 'group', group });
    for (const member of group.members) {
      claimed.add(member);
      out.push({ kind: 'supplier', supplier: member });
    }
  }
  for (const s of suppliers) {
    if (!claimed.has(s)) out.push({ kind: 'supplier', supplier: s });
  }
  return out;
}
